import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import PDFViewer from './PDFViewer';

const PDFManagement = ({ selectedCompany, onViewEmployee }) => {
    const [pdfs, setPdfs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadData, setUploadData] = useState({
        company_id: '',
        document_type: 'other',
        description: '',
        related_entity_type: '',
        related_entity_id: '',
        tags: ''
    });
    const [companies, setCompanies] = useState([]);
    const [filters, setFilters] = useState({
        company_id: '',
        document_type: '',
        filename_pattern: '',
        page: 1,
        page_size: 10
    });
    const [pagination, setPagination] = useState({
        total_count: 0,
        page: 1,
        page_size: 10
    });
    const [selectedPDF, setSelectedPDF] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [reindexing, setReindexing] = useState(false);

    const handleReindexExisting = async () => {
        setReindexing(true);
        try {
            const response = await fetch(`${backendUrl}/api/pdf/reindex-existing`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            if (response.ok) {
                const data = await response.json();
                const d = data.details || {};
                toast.success(`${data.message} (contrats: ${d.contracts || 0}, docs RH: ${d.hr_documents || 0}, pièces admin: ${d.admin_documents || 0}, déjà indexés: ${d.skipped || 0})`);
                loadPDFs();
            } else {
                const err = await response.json().catch(() => ({}));
                toast.error(err.detail || "Erreur lors de l'indexation rétroactive");
            }
        } catch (error) {
            toast.error("Erreur lors de l'indexation rétroactive");
        } finally {
            setReindexing(false);
        }
    };
    const [showViewModal, setShowViewModal] = useState(false);
    const [showPDFViewer, setShowPDFViewer] = useState(false);
    const [viewerDocumentId, setViewerDocumentId] = useState(null);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Synchroniser les filtres avec l'entreprise sélectionnée
    useEffect(() => {
        if (selectedCompany) {
            setFilters(prev => ({ ...prev, company_id: selectedCompany.id }));
            setUploadData(prev => ({ ...prev, company_id: selectedCompany.id }));
        }
    }, [selectedCompany]);

    useEffect(() => {
        loadCompanies();
        loadPDFs();
    }, [filters]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Authorization': `Bearer ${token}`,
        };
    };

    const loadCompanies = async () => {
        try {
            const response = await fetch(`${backendUrl}/api/companies`, {
                headers: getAuthHeaders(),
            });
            if (response.ok) {
                const companiesData = await response.json();
                setCompanies(companiesData);
                if (companiesData.length > 0 && !uploadData.company_id) {
                    setUploadData(prev => ({ ...prev, company_id: companiesData[0].id }));
                }
            }
        } catch (error) {
            console.error('Erreur lors du chargement des entreprises:', error);
        }
    };

    const loadPDFs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const response = await fetch(`${backendUrl}/api/pdf/list?${params}`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                setPdfs(data.documents);
                setPagination({
                    total_count: data.total_count,
                    page: data.page,
                    page_size: data.page_size
                });
            } else {
                toast.error('Erreur lors du chargement des PDFs');
            }
        } catch (error) {
            console.error('Erreur lors du chargement des PDFs:', error);
            toast.error('Erreur lors du chargement des PDFs');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toast.error('Seuls les fichiers PDF sont acceptés');
                return;
            }
            if (file.size > 50 * 1024 * 1024) { // 50MB limite
                toast.error('Le fichier est trop volumineux (max 50MB)');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error('Veuillez sélectionner un fichier PDF');
            return;
        }
        if (!uploadData.company_id) {
            toast.error('Veuillez sélectionner une entreprise');
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('company_id', uploadData.company_id);
        formData.append('document_type', uploadData.document_type);
        if (uploadData.description) formData.append('description', uploadData.description);
        if (uploadData.related_entity_type) formData.append('related_entity_type', uploadData.related_entity_type);
        if (uploadData.related_entity_id) formData.append('related_entity_id', uploadData.related_entity_id);
        if (uploadData.tags) formData.append('tags', JSON.stringify(uploadData.tags.split(',').map(t => t.trim())));

        try {
            const response = await fetch(`${backendUrl}/api/pdf/upload`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                toast.success('Fichier PDF uploadé et traité avec succès !');
                setSelectedFile(null);
                setUploadData(prev => ({ ...prev, description: '', tags: '' }));
                setShowUploadModal(false);
                loadPDFs();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de l\'upload');
            }
        } catch (error) {
            console.error('Erreur lors de l\'upload:', error);
            toast.error('Erreur lors de l\'upload du fichier');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (pdf) => {
        try {
            const response = await fetch(`${backendUrl}/api/pdf/${pdf.id}/download`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = pdf.original_filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Téléchargement terminé');
            } else {
                toast.error('Erreur lors du téléchargement');
            }
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleDelete = async (pdfId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document PDF ?')) {
            return;
        }

        try {
            const response = await fetch(`${backendUrl}/api/pdf/${pdfId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                toast.success('Document supprimé avec succès');
                loadPDFs();
            } else {
                toast.error('Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleViewPDF = async (pdf) => {
        try {
            const response = await fetch(`${backendUrl}/api/pdf/${pdf.id}`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const pdfData = await response.json();
                setSelectedPDF(pdfData);
                setShowViewModal(true);
            } else {
                toast.error('Erreur lors de la récupération des informations PDF');
            }
        } catch (error) {
            console.error('Erreur lors de la récupération PDF:', error);
            toast.error('Erreur lors de la récupération des informations PDF');
        }
    };

    const getDocumentTypeLabel = (type) => {
        const types = {
            'bulletin': 'Bulletin de paie',
            'contract': 'Contrat',
            'administrative': 'Document administratif',
            'import_data': 'Import de données',
            'other': 'Autre'
        };
        return types[type] || type;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Gestion des Documents PDF</h2>
                    <div className="flex flex-wrap gap-3 mt-2">
                        {selectedCompany && (
                            <p className="text-sm text-gray-600">
                                📁 Entreprise sélectionnée : <span className="font-semibold text-blue-600">{selectedCompany.name}</span>
                            </p>
                        )}
                        {filters.document_type && (
                            <p className="text-sm text-gray-600">
                                📄 Type de document : <span className="font-semibold text-green-600">
                                    {filters.document_type === 'bulletin' && 'Bulletin de paie'}
                                    {filters.document_type === 'contract' && 'Contrat'}
                                    {filters.document_type === 'administrative' && 'Document administratif'}
                                    {filters.document_type === 'import_data' && 'Import de données'}
                                    {filters.document_type === 'other' && 'Autre'}
                                </span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="reindex-existing-btn"
                        onClick={handleReindexExisting}
                        disabled={reindexing}
                        className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                        {reindexing ? '⏳ Indexation...' : '🔄 Indexer l\'existant'}
                    </button>
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        📄 Uploader un PDF
                    </button>
                </div>
            </div>

            {/* Filtres */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entreprise
                    </label>
                    <select
                        value={filters.company_id}
                        onChange={(e) => setFilters(prev => ({ ...prev, company_id: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        disabled={!!selectedCompany}
                    >
                        <option value="">Toutes les entreprises</option>
                        {companies.map(company => (
                            <option key={company.id} value={company.id}>
                                {company.name}
                            </option>
                        ))}
                    </select>
                    {selectedCompany && (
                        <p className="text-xs text-blue-600 mt-1">
                            ℹ️ Filtré automatiquement par l'entreprise sélectionnée
                        </p>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de document
                    </label>
                    <select
                        value={filters.document_type}
                        onChange={(e) => setFilters(prev => ({ ...prev, document_type: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                        <option value="">Tous les types</option>
                        <option value="bulletin">Bulletin de paie</option>
                        <option value="contract">Contrat</option>
                        <option value="administrative">Document administratif</option>
                        <option value="import_data">Import de données</option>
                        <option value="other">Autre</option>
                    </select>
                    {filters.document_type && (
                        <p className="text-xs text-green-600 mt-1">
                            ✓ Filtré par type de document
                        </p>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rechercher par nom
                    </label>
                    <input
                        type="text"
                        value={filters.filename_pattern}
                        onChange={(e) => setFilters(prev => ({ ...prev, filename_pattern: e.target.value }))}
                        placeholder="Nom du fichier..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                </div>
            </div>

            {/* Liste des PDFs */}
            {loading ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2">Chargement...</p>
                </div>
            ) : pdfs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    Aucun document PDF trouvé
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-300">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nom du fichier
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Taille
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Extraction
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {pdfs.map((pdf) => (
                                    <tr key={pdf.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {pdf.original_filename}
                                            </div>
                                            {pdf.description && (
                                                <div className="text-sm text-gray-500">
                                                    {pdf.description}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {getDocumentTypeLabel(pdf.document_type)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatFileSize(pdf.file_size)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(pdf.created_at)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                pdf.extraction_status === 'completed' ? 'bg-green-100 text-green-800' :
                                                pdf.extraction_status === 'failed' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {pdf.extraction_status === 'completed' ? 'Terminé' :
                                                 pdf.extraction_status === 'failed' ? 'Échoué' :
                                                 'En attente'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleViewPDF(pdf)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Voir les détails"
                                                >
                                                    👁️
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setViewerDocumentId(pdf.id);
                                                        setShowPDFViewer(true);
                                                    }}
                                                    className="text-purple-600 hover:text-purple-900"
                                                    title="Visionner le PDF"
                                                >
                                                    📖
                                                </button>
                                                <button
                                                    onClick={() => handleDownload(pdf)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="Télécharger"
                                                >
                                                    📥
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(pdf.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Supprimer"
                                                >
                                                    🗑️
                                                </button>
                                                {onViewEmployee && pdf.related_entity_type === 'employee' && pdf.related_entity_id && (
                                                    <button
                                                        data-testid={`view-employee-${pdf.id}`}
                                                        onClick={() => onViewEmployee(pdf)}
                                                        className="text-orange-600 hover:text-orange-900"
                                                        title="Voir la fiche employé"
                                                    >
                                                        👤
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.total_count > pagination.page_size && (
                        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                            <div className="flex flex-1 justify-between sm:hidden">
                                <button
                                    onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                    disabled={filters.page <= 1}
                                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                                >
                                    Précédent
                                </button>
                                <button
                                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={filters.page >= Math.ceil(pagination.total_count / pagination.page_size)}
                                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                                >
                                    Suivant
                                </button>
                            </div>
                            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Affichage de {((filters.page - 1) * filters.page_size) + 1} à {Math.min(filters.page * filters.page_size, pagination.total_count)} sur {pagination.total_count} documents
                                    </p>
                                </div>
                                <div>
                                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                        <button
                                            onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                            disabled={filters.page <= 1}
                                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                                        >
                                            Précédent
                                        </button>
                                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                                            {filters.page}
                                        </span>
                                        <button
                                            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                            disabled={filters.page >= Math.ceil(pagination.total_count / pagination.page_size)}
                                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:bg-gray-100"
                                        >
                                            Suivant
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Modal d'upload */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Uploader un document PDF</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fichier PDF *
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileSelect}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                                {selectedFile && (
                                    <p className="text-sm text-gray-600 mt-1">
                                        Fichier sélectionné: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Entreprise *
                                </label>
                                <select
                                    value={uploadData.company_id}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, company_id: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="">Sélectionner une entreprise</option>
                                    {companies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Type de document
                                </label>
                                <select
                                    value={uploadData.document_type}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, document_type: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="bulletin">Bulletin de paie</option>
                                    <option value="contract">Contrat</option>
                                    <option value="administrative">Document administratif</option>
                                    <option value="import_data">Import de données</option>
                                    <option value="other">Autre</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={uploadData.description}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                                    rows="2"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    placeholder="Description du document..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tags (séparés par des virgules)
                                </label>
                                <input
                                    type="text"
                                    value={uploadData.tags}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, tags: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    placeholder="tag1, tag2, tag3..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setSelectedFile(null);
                                }}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                            >
                                {loading ? 'Upload en cours...' : 'Uploader'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de visualisation */}
            {showViewModal && selectedPDF && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-10 mx-auto p-5 border w-3/4 max-w-4xl shadow-lg rounded-md bg-white">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-900">
                                Détails du PDF: {selectedPDF.original_filename}
                            </h3>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Informations générales */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Informations générales</h4>
                                <div className="space-y-2 text-sm">
                                    <p><strong>Nom original:</strong> {selectedPDF.original_filename}</p>
                                    <p><strong>Type:</strong> {getDocumentTypeLabel(selectedPDF.document_type)}</p>
                                    <p><strong>Taille:</strong> {formatFileSize(selectedPDF.file_size)}</p>
                                    <p><strong>Date d'upload:</strong> {formatDate(selectedPDF.created_at)}</p>
                                    <p><strong>Statut d'extraction:</strong> 
                                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            selectedPDF.extraction_status === 'completed' ? 'bg-green-100 text-green-800' :
                                            selectedPDF.extraction_status === 'failed' ? 'bg-red-100 text-red-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {selectedPDF.extraction_status === 'completed' ? 'Terminé' :
                                             selectedPDF.extraction_status === 'failed' ? 'Échoué' :
                                             'En attente'}
                                        </span>
                                    </p>
                                    {selectedPDF.description && (
                                        <p><strong>Description:</strong> {selectedPDF.description}</p>
                                    )}
                                    {selectedPDF.tags && selectedPDF.tags.length > 0 && (
                                        <p><strong>Tags:</strong> {selectedPDF.tags.join(', ')}</p>
                                    )}
                                </div>
                            </div>

                            {/* Données extraites */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Données extraites</h4>
                                {selectedPDF.extracted_text ? (
                                    <div className="bg-gray-50 p-3 rounded-lg max-h-64 overflow-y-auto">
                                        <h5 className="text-sm font-medium mb-2">Texte extrait (aperçu):</h5>
                                        <p className="text-xs text-gray-600 whitespace-pre-wrap">
                                            {selectedPDF.extracted_text.substring(0, 500)}
                                            {selectedPDF.extracted_text.length > 500 && '...'}
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">Aucun texte extrait disponible</p>
                                )}
                                
                                {selectedPDF.extracted_data && Object.keys(selectedPDF.extracted_data).length > 0 && (
                                    <div className="mt-4 bg-blue-50 p-3 rounded-lg max-h-64 overflow-y-auto">
                                        <h5 className="text-sm font-medium mb-2">Données structurées:</h5>
                                        <pre className="text-xs text-gray-600">
                                            {JSON.stringify(selectedPDF.extracted_data, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => handleDownload(selectedPDF)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Télécharger
                            </button>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Viewer */}
            {showPDFViewer && viewerDocumentId && (
                <PDFViewer 
                    documentId={viewerDocumentId}
                    onClose={() => {
                        setShowPDFViewer(false);
                        setViewerDocumentId(null);
                    }}
                />
            )}
        </div>
    );
};

export default PDFManagement;